import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
  HStack,
  Checkbox,
  Badge,
  Icon,
  useDisclosure,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  IconButton,
} from '@chakra-ui/react'
import { FaChevronDown, FaSearch, FaTimes, FaPlus } from 'react-icons/fa'

interface ExchangeSymbolCascaderProps {
  groupedData: Record<string, string[]>
  selectedValues: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  isInvalid?: boolean
  isMulti?: boolean 
}

const SymbolItem = React.memo(({ 
  symbol, 
  exchange, 
  isChecked, 
  isMulti, 
  onToggle,
  isCustom = false
}: { 
  symbol: string, 
  exchange: string, 
  isChecked: boolean, 
  isMulti: boolean,
  onToggle: (exchange: string, symbol: string) => void,
  isCustom?: boolean
}) => {
  return (
    <Flex
      as="label"
      cursor="pointer"
      px={3}
      py={2}
      borderRadius="md"
      _hover={{ bg: 'gray.50' }}
      align="center"
      onClick={(e) => {
        // Usually default label click is fine for checkbox, but for custom/single we handle manually
        if (!isMulti || isCustom) {
          e.preventDefault()
          onToggle(exchange, symbol)
        }
      }}
    >
      {isMulti ? (
        <Checkbox
          isChecked={isChecked}
          onChange={() => onToggle(exchange, symbol)}
          colorScheme="blue"
          mr={3}
          pointerEvents="none"
        />
      ) : (
        <Box mr={3} w="16px" h="16px" display="flex" alignItems="center" justifyContent="center">
            {isChecked && <Box w="8px" h="8px" bg="blue.500" borderRadius="full" />}
            {isCustom && !isChecked && <Icon as={FaPlus} color="gray.400" w={3} h={3} />}
        </Box>
      )}
      <Text fontSize="sm" fontWeight={isChecked ? "semibold" : "normal"}>
        {isCustom ? `Use "${symbol}"` : symbol}
      </Text>
    </Flex>
  )
})

// Memoized Multi-select Trigger Component
const MultiSelectTrigger = React.memo(({
  selectedValues,
  placeholder,
  isInvalid,
  onOpen,
  onRemove,
}: {
  selectedValues: string[]
  placeholder: string
  isInvalid: boolean
  onOpen: () => void
  onRemove: (exchange: string, symbol: string) => void
}) => (
  <Button
    rightIcon={<FaChevronDown />}
    w="100%"
    textAlign="left"
    variant="outline"
    fontWeight="normal"
    borderColor={isInvalid ? 'red.500' : undefined}
    _invalid={{ borderColor: 'red.500', boxShadow: 'none' }}
    h="auto"
    minH="40px"
    py={2}
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    onClick={onOpen}
  >
    <Box flex="1" overflow="hidden">
      {selectedValues.length > 0 ? (
        <Wrap spacing={1}>
          {selectedValues.slice(0, 5).map(val => {
            const separatorIndex = val.indexOf('_');
            const ex = val.substring(0, separatorIndex);
            const sym = val.substring(separatorIndex + 1);
            return (
              <WrapItem key={val}>
                <Tag size="sm" borderRadius="full" variant="solid" colorScheme="blue">
                  <TagLabel>{sym}</TagLabel>
                  <TagCloseButton onClick={(e) => {
                    e.stopPropagation()
                    onRemove(ex, sym)
                  }} />
                </Tag>
              </WrapItem>
            )
          })}
          {selectedValues.length > 5 && (
            <WrapItem>
              <Tag size="sm" borderRadius="full" variant="subtle">
                +{selectedValues.length - 5} more
              </Tag>
            </WrapItem>
          )}
        </Wrap>
      ) : (
        <Text color="gray.500">{placeholder}</Text>
      )}
    </Box>
  </Button>
))

// Memoized Single-select Trigger Component
const SingleSelectTrigger = React.memo(({
  inputValue,
  placeholder,
  isInvalid,
  onOpen,
  onChange,
  onKeyDown,
  onBlur,
  onClear,
}: {
  inputValue: string
  placeholder: string
  isInvalid: boolean
  onOpen: () => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onBlur: () => void
  onClear: () => void
}) => (
  <InputGroup onClick={onOpen}>
    <Input
      value={inputValue}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      placeholder={placeholder}
      borderColor={isInvalid ? 'red.500' : undefined}
      _focus={{ borderColor: 'blue.400' }}
    />
    <InputRightElement>
      {inputValue ? (
        <IconButton
          aria-label="Clear"
          icon={<FaTimes />}
          size="xs"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation()
            onClear()
          }}
        />
      ) : (
        <Icon as={FaChevronDown} color="gray.500" fontSize="sm" />
      )}
    </InputRightElement>
  </InputGroup>
))

export const ExchangeSymbolCascader: React.FC<ExchangeSymbolCascaderProps> = ({
  groupedData,
  selectedValues,
  onChange,
  placeholder = "Select assets",
  isInvalid = false,
  isMulti = true,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [activeExchange, setActiveExchange] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  // Main input value (for single select/custom input)
  const [inputValue, setInputValue] = useState('') 
  const initialFocusRef = useRef<HTMLInputElement>(null)
  
  const exchanges = useMemo(() => Object.keys(groupedData).sort(), [groupedData])

  // Initial active exchange
  useEffect(() => {
    if (!activeExchange && exchanges.length > 0) {
      setActiveExchange(exchanges[0])
    }
  }, [exchanges, activeExchange])

  // Sync inputValue with selectedValues in Single Mode
  useEffect(() => {
    if (!isMulti) {
      setInputValue(selectedValues.length > 0 ? selectedValues[0] : '')
    }
  }, [selectedValues, isMulti])

  // Filter symbols
  const filteredSymbols = useMemo(() => {
    if (!activeExchange) return []
    const symbols = groupedData[activeExchange] || []
    if (!searchQuery) return symbols
    return symbols.filter(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
  }, [activeExchange, groupedData, searchQuery])

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues])

  const handleToggle = useCallback((exchange: string, symbol: string) => {
    const fullValue = `${exchange}_${symbol}`
    
    if (!isMulti) {
      onChange([fullValue])
      setInputValue(fullValue) // Update display immediately with full value
      onClose()
      return
    }

    if (selectedSet.has(fullValue)) {
      onChange(selectedValues.filter(v => v !== fullValue))
    } else {
      onChange([...selectedValues, fullValue])
    }
  }, [isMulti, onChange, onClose, selectedSet, selectedValues])

  const handleClearAll = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange([])
    setInputValue('')
  }, [onChange])

  // Handle main input changes
  const handleMainInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)
    if (!isOpen) onOpen()
  }, [isOpen, onOpen])

  // Handle Enter in main input
  const handleMainInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // If input is empty, maybe clear?
      if (!inputValue.trim()) {
        onChange([])
        return
      }
      // Commit current input as value directly (no exchange prefix)
      onChange([inputValue.trim()])
      onClose()
    }
  }, [inputValue, onChange, onClose])

  // Handle blur on main input - commit the value when user clicks away
  const handleMainInputBlur = useCallback(() => {
    // Only commit if there's a value and it's different from current selection
    if (inputValue.trim()) {
      onChange([inputValue.trim()])
    }
  }, [inputValue, onChange])

  const selectedCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    selectedValues.forEach(val => {
      const separatorIndex = val.indexOf('_');
      const ex = separatorIndex !== -1 ? val.substring(0, separatorIndex) : val;
      counts[ex] = (counts[ex] || 0) + 1
    })
    return counts
  }, [selectedValues])


  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-start"
      isLazy
      matchWidth
      initialFocusRef={isMulti ? initialFocusRef : undefined}
      returnFocusOnClose={false}
      autoFocus={false}
    >
      <PopoverTrigger>
        <Box w="100%">
          {isMulti ? (
            <MultiSelectTrigger
              selectedValues={selectedValues}
              placeholder={placeholder}
              isInvalid={isInvalid}
              onOpen={onOpen}
              onRemove={handleToggle}
            />
          ) : (
            <SingleSelectTrigger
              inputValue={inputValue}
              placeholder={placeholder}
              isInvalid={isInvalid}
              onOpen={onOpen}
              onChange={handleMainInputChange}
              onKeyDown={handleMainInputKeyDown}
              onBlur={handleMainInputBlur}
              onClear={handleClearAll}
            />
          )}
        </Box>
      </PopoverTrigger>
      
      <PopoverContent w="450px" maxW="90vw" boxShadow="xl">
        <PopoverBody p={0}>
          <Flex h="320px">
            {/* Left Column: Exchanges */}
            <Box w="35%" borderRightWidth="1px" bg="gray.50" overflowY="auto" py={2}>
              {exchanges.map(ex => (
                <Flex
                  key={ex}
                  px={3}
                  py={3}
                  cursor="pointer"
                  bg={activeExchange === ex ? 'white' : 'transparent'}
                  borderLeftWidth="3px"
                  borderLeftColor={activeExchange === ex ? 'blue.500' : 'transparent'}
                  _hover={{ bg: activeExchange === ex ? 'white' : 'gray.100' }}
                  onClick={() => {
                    setActiveExchange(ex)
                    setSearchQuery('')
                  }}
                  justify="space-between"
                  align="center"
                >
                  <Text fontWeight={activeExchange === ex ? 'bold' : 'normal'} fontSize="sm">
                    {ex}
                  </Text>
                  {selectedCounts[ex] > 0 && (
                    <Badge colorScheme="blue" borderRadius="full" fontSize="xs">
                      {selectedCounts[ex]}
                    </Badge>
                  )}
                </Flex>
              ))}
            </Box>

            {/* Right Column: Symbols */}
            <Box w="65%" display="flex" flexDirection="column">
              {/* Search Header - Keep it for searching within list explicitly if needed, 
                  OR sync it with main input? 
                  For now, let's keep it separate to search *within* the active exchange 
                  without changing the main input (which is the final value).
              */}
              <Box p={3} borderBottomWidth="1px">
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FaSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    ref={initialFocusRef}
                    placeholder={`Filter ${activeExchange}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    borderRadius="md"
                  />
                </InputGroup>
                <Flex mt={2} justify="space-between" align="center">
                  <Text fontSize="xs" color="gray.500">
                    {filteredSymbols.length} symbols
                  </Text>
                  {isMulti && (
                    <HStack>
                        {/* ... multi select actions ... */}
                    </HStack>
                  )}
                </Flex>
              </Box>

              {/* Symbol List */}
              <Box flex="1" overflowY="auto" p={2}>
                <VStack align="stretch" spacing={0}>
                  {/* If main input has value and it's not in list, show option to add it? 
                      Actually, filteredSymbols is based on `searchQuery` (the internal filter), 
                      not `inputValue` (the main input).
                  */}
                  
                  {filteredSymbols.length > 0 ? (
                    filteredSymbols.map(sym => (
                        <SymbolItem
                            key={sym}
                            symbol={sym}
                            exchange={activeExchange}
                            isChecked={selectedSet.has(`${activeExchange}_${sym}`)}
                            isMulti={isMulti}
                            onToggle={handleToggle}
                        />
                    ))
                  ) : (
                    <Flex justify="center" align="center" h="100%" color="gray.400">
                      No options
                    </Flex>
                  )}
                </VStack>
              </Box>
            </Box>
          </Flex>
          
           {/* Footer Actions - Only shown if isMulti is true */}
           {isMulti && (
            <Box p={3} borderTopWidth="1px" bg="gray.50" borderBottomRadius="md">
               <Flex justify="space-between" align="center">
                  <Button 
                      size="sm" 
                      variant="ghost" 
                      colorScheme="red" 
                      onClick={() => onChange([])}
                      visibility={selectedValues.length > 0 ? 'visible' : 'hidden'}
                  >
                    Clear All
                  </Button>
                  <Button size="sm" colorScheme="blue" onClick={onClose}>
                    Done
                  </Button>
               </Flex>
            </Box>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
